import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, PageHero, SpecTable, CTASection, usePageMeta, Eyebrow } from "../components/ui-bits";
import { IMG, products } from "../data/content";
import { useCmsList } from "../hooks/useCmsContent";

const ProductRow = ({ p, flip }) => (
  <article id={p.id} className="scroll-mt-28 border-b border-slate-200 last:border-b-0" data-testid={`product-${p.id}`}>
    <div className={`container-gre py-16 lg:py-20 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center`}>
      <Reveal className={flip ? "lg:order-2" : ""}>
        <div className="relative">
          <img src={IMG(p.img)} alt={p.name} loading="lazy" className="w-full aspect-[4/3] object-cover img-industrial border border-slate-200" />
          <div className={`absolute top-0 ${flip ? "-right-3" : "-left-3"} w-14 h-14 border-t-4 ${flip ? "border-r-4" : "border-l-4"} border-[#C5221F]`} />
        </div>
      </Reveal>
      <div className={flip ? "lg:order-1" : ""}>
        <Reveal><Eyebrow>Product Range</Eyebrow></Reveal>
        <Reveal delay={0.06}>
          <h2 className="font-display mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{p.name}</h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-4 text-base leading-relaxed text-slate-600">{p.desc}</p>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="mt-7"><SpecTable rows={p.specs} /></div>
        </Reveal>
        <Reveal delay={0.24}>
          <div className="mt-6 space-y-3">
            <p className="text-[13px] text-slate-600"><span className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Materials of Construction</span>{p.moc}</p>
            <p className="text-[13px] text-slate-600"><span className="font-mono-tech text-[10px] font-bold uppercase tracking-widest text-slate-400 block mb-1">Typical Applications</span>{p.applications}</p>
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <Link to={`/contact?interest=${encodeURIComponent(p.name)}`} data-testid={`product-enquire-${p.id}`} className="group mt-7 inline-flex items-center gap-2.5 text-sm font-semibold uppercase tracking-widest text-[#C5221F]">
            Enquire about {p.name} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
  </article>
);

const Products = () => {
  const prods = useCmsList("product", products);
  usePageMeta(
    "Products — Pressure Vessels, Columns, Heat Exchangers, Spheres | G. R. Engineering",
    "High pressure vessels to 425 MT, columns & towers to 110 m, shell & tube and Helix heat exchangers, reactors & regenerators, mounded storage bullets and Horton spheres — ASME 'U' & 'U2' stamped."
  );
  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, []);
  return (
    <>
      <PageHero
        img={IMG("exchanger-a")}
        eyebrow="Products"
        title="Critical pressure equipment, engineered & fabricated in-house"
        sub="From 425 MT single-piece pressure vessels to complete reactor–regenerator packages — every product is fabricated at our Tarapur works or at site, under one quality system."
      />
      <section className="bg-white border-b border-slate-200" data-testid="products-index">
        <div className="container-gre py-10 flex flex-wrap gap-x-8 gap-y-3">
          {prods.map((p) => (
            <a key={p.id} href={`#${p.id}`} className="font-mono-tech text-xs font-semibold uppercase tracking-widest text-slate-500 hover:text-[#C5221F] transition-colors duration-200">
              {p.name}
            </a>
          ))}
        </div>
      </section>
      <section className="bg-white" data-testid="products-list">
        {prods.map((p, i) => <ProductRow key={p.id} p={p} flip={i % 2 === 1} />)}
      </section>
      <CTASection />
    </>
  );
};

export default Products;
